/**
 * localStorage key for the Suggester recents list. Distinct from
 * `metro:routeSelection` (the host's own persistence) so the Suggester
 * owns its recents independently — the package never reaches into the
 * host store.
 */
export const RECENTS_KEY = 'metro:suggester:recents' as const

/**
 * Maximum number of recent station ids retained — N=8 per spec §3.
 */
export const RECENTS_CAP = 8

/**
 * Prepend `id` to `list`, deduplicating (any prior occurrence is removed
 * so the id ends at the head exactly once) and capping to {@link RECENTS_CAP}.
 *
 * Pure: returns a fresh array, never mutates `list`.
 */
export function pushRecent(list: readonly string[], id: string): string[] {
  return [id, ...list.filter((entry) => entry !== id)].slice(0, RECENTS_CAP)
}

function isStringArray(value: unknown): value is string[] {
  return Array.isArray(value) && value.every((e) => typeof e === 'string')
}

/**
 * Read the recents list from localStorage.
 *
 * Returns `[]` when nothing is stored, the payload is not valid JSON, the
 * payload is not a string array, or localStorage access throws (private
 * mode / disabled storage). An over-long stored list is capped on load.
 * Never throws.
 */
export function loadRecents(): readonly string[] {
  try {
    const raw = localStorage.getItem(RECENTS_KEY)
    if (raw === null) return []
    const parsed = JSON.parse(raw) as unknown
    return isStringArray(parsed) ? parsed.slice(0, RECENTS_CAP) : []
  } catch {
    return []
  }
}

/**
 * Persist the recents list to localStorage, capped to {@link RECENTS_CAP}.
 *
 * Best-effort: swallows `JSON.stringify` and `setItem` errors (quota,
 * private mode). The Suggester must remain functional without persistence.
 */
export function saveRecents(list: readonly string[]): void {
  try {
    localStorage.setItem(RECENTS_KEY, JSON.stringify(list.slice(0, RECENTS_CAP)))
  } catch {
    // localStorage may be unavailable (private mode, quota). Drop silently.
  }
}
